import { createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import type { LayoutCard } from '@ocktra/shared-types';
import { CARD_TYPES_MAP } from '../config/cardConfig';

export interface Card {
    id: string;
    type: string;
    title: string;
    content: string;
    position: { x: number; y: number };
    size: { width: number; height: number };
    isMaximized: boolean;
    isMinimized: boolean;
    zIndex: number;
}

export interface SavedLayout {
    id: string;
    name: string;
    cards: Card[];
    createdAt: string;
}

export interface CardsState {
    cards: Card[];
    highestZIndex: number;
    nextCardId: number;
    blinkingCardId: string | null;
    savedLayouts: SavedLayout[];
    activeLayoutId: string | null;
}

const initialState: CardsState = {
    cards: [
    ],
    highestZIndex: 2,
    nextCardId: 3,
    blinkingCardId: null, savedLayouts: [],
    activeLayoutId: null,
};

// Export cardTypes from centralized config for backward compatibility
export const cardTypes = CARD_TYPES_MAP;

const cardsSlice = createSlice({
    name: 'cards',
    initialState,
    reducers: {
        addCard: (state, action: PayloadAction<LayoutCard>) => {


            const incomingCard = action.payload;
            console.log("Adding card with layout config:", incomingCard);

            const cardInfo = cardTypes[action.payload.type] || { title: 'New Card', content: 'default' };

            // Check if card with this title already exists
            const existingCard = state.cards.find(card => card.title === cardInfo.title);
            if (existingCard) {
                // Restore if minimized
                if (existingCard.isMinimized) {
                    existingCard.isMinimized = false;
                    existingCard.isMaximized = false;
                }
                // Bring to front and blink
                state.highestZIndex += 1;
                existingCard.zIndex = state.highestZIndex;
                state.blinkingCardId = existingCard.id;
                return;
            }

            // Add new card
            const newCard: Card = {
                id: String(state.nextCardId),
                type: cardInfo.type,
                title: cardInfo.title,
                content: cardInfo.content,
                position: { x: incomingCard.position.x, y: incomingCard.position.y },
                size: { width: incomingCard.size.width, height: incomingCard.size.height },
                isMaximized: false,
                isMinimized: false,
                zIndex: state.highestZIndex + 1
            };

            state.cards.push(newCard);
            state.highestZIndex += 1;
            state.nextCardId += 1;
        },
        addCardById: (state, action: PayloadAction<string>) => {
            // This reducer is needed for dispatching by id from components

            const cardInfo = cardTypes[action.payload];

            if (!cardInfo) {
                console.warn(`Card type '${action.payload}' not found in configuration.`);
                return;
            }

            // Check if card with this title already exists
            const existingCard = state.cards.find(card => card.type === cardInfo.type);
            if (existingCard) {
                // Restore if minimized
                if (existingCard.isMinimized) {
                    existingCard.isMinimized = false;
                    existingCard.isMaximized = false;
                }
                // Bring to front and blink
                state.highestZIndex += 1;
                existingCard.zIndex = state.highestZIndex;
                state.blinkingCardId = existingCard.id;
                return;
            }

            // Add new card with default position and size
            const newCard: Card = {
                id: String(state.nextCardId),
                type: cardInfo.type,
                title: cardInfo.title,
                content: cardInfo.content,
                position: { x: 100, y: 100 },
                size: { width: 400, height: 300 },
                isMaximized: false,
                isMinimized: false,
                zIndex: state.highestZIndex + 1
            };

            state.cards.push(newCard);
            state.highestZIndex += 1;
            state.nextCardId += 1;
        },

        removeCard: (state, action: PayloadAction<string>) => {
            state.cards = state.cards.filter(card => card.id !== action.payload);
        },

        updateCardPosition: (state, action: PayloadAction<{ id: string; position: { x: number; y: number } }>) => {
            const card = state.cards.find(c => c.id === action.payload.id);
            if (card) {
                card.position = action.payload.position;
            }
        },

        updateCardSize: (state, action: PayloadAction<{ id: string; size: { width: number; height: number }; position?: { x: number; y: number } }>) => {
            const card = state.cards.find(c => c.id === action.payload.id);
            if (card) {
                card.size = action.payload.size;
                if (action.payload.position) {
                    card.position = action.payload.position;
                }
            }
        },

        bringCardToFront: (state, action: PayloadAction<string>) => {
            const card = state.cards.find(c => c.id === action.payload);
            if (card) {
                state.highestZIndex += 1;
                card.zIndex = state.highestZIndex;
            }
        },

        toggleMaximize: (state, action: PayloadAction<string>) => {
            const card = state.cards.find(c => c.id === action.payload);
            if (card) {
                const newIsMaximized = !card.isMaximized;

                // Update all cards
                state.cards.forEach(c => {
                    if (c.id === action.payload) {
                        c.isMaximized = newIsMaximized;
                        c.isMinimized = false;
                    } else {
                        c.isMaximized = false;
                        c.isMinimized = false;
                    }
                });

                // Bring to front
                state.highestZIndex += 1;
                card.zIndex = state.highestZIndex;
            }
        },

        toggleMinimize: (state, action: PayloadAction<string>) => {
            const card = state.cards.find(c => c.id === action.payload);
            if (card) {
                card.isMinimized = !card.isMinimized;
                card.isMaximized = false;
            }
        },

        setBlinkingCard: (state, action: PayloadAction<string | null>) => {
            state.blinkingCardId = action.payload;
        },

        updateCards: (state, action: PayloadAction<any[]>) => {
            // Apply layout by matching card titles
            action.payload.forEach((layoutConfig) => {
                // Find card by title if provided, otherwise use index
                let card;
                if (layoutConfig.title) {
                    card = state.cards.find(c => c.title === layoutConfig.title);
                } else if (layoutConfig.index !== undefined) {
                    card = state.cards[layoutConfig.index];
                }

                if (card) {
                    card.position = layoutConfig.position;
                    card.size = layoutConfig.size;
                    card.isMaximized = layoutConfig.isMaximized;
                    card.isMinimized = layoutConfig.isMinimized;
                    card.zIndex = layoutConfig.zIndex;
                }
            });
        },

        clearAllCards: (state) => {
            state.cards = [];
            state.highestZIndex = 0;
        },

        saveCurrentLayout: (state, action: PayloadAction<string>) => {
            const newLayout: SavedLayout = {
                id: Date.now().toString(),
                name: action.payload,
                cards: JSON.parse(JSON.stringify(state.cards)), // Deep clone
                createdAt: new Date().toISOString(),
            };
            state.savedLayouts.push(newLayout);
            state.activeLayoutId = newLayout.id;
        },

        loadLayout: (state, action: PayloadAction<string>) => {
            const layout = state.savedLayouts.find(l => l.id === action.payload);
            if (layout) {
                state.cards = JSON.parse(JSON.stringify(layout.cards)); // Deep clone
                state.activeLayoutId = layout.id;
                // Recalculate highest zIndex
                state.highestZIndex = Math.max(...state.cards.map(c => c.zIndex), 0);
            }
        },

        deleteLayout: (state, action: PayloadAction<string>) => {
            state.savedLayouts = state.savedLayouts.filter(l => l.id !== action.payload);
            if (state.activeLayoutId === action.payload) {
                state.activeLayoutId = null;
            }
        },

        renameLayout: (state, action: PayloadAction<{ id: string; name: string }>) => {
            const layout = state.savedLayouts.find(l => l.id === action.payload.id);
            if (layout) {
                layout.name = action.payload.name;
            }
        },

        setActiveLayout: (state, action: PayloadAction<string | null>) => {
            state.activeLayoutId = action.payload;
        },
    },
});

export const {
    addCard,
    addCardById,
    removeCard,
    updateCardPosition,
    updateCardSize,
    bringCardToFront,
    toggleMaximize,
    toggleMinimize,
    setBlinkingCard,
    updateCards,
    clearAllCards,
    saveCurrentLayout,
    loadLayout,
    deleteLayout,
    renameLayout,
    setActiveLayout,
} = cardsSlice.actions;

export default cardsSlice.reducer;
